// Remote support on the web client (shared/REMOTE-SUPPORT.md §1/§6; the web is the HELPER only):
//  - `RemoteSupportHost` (mounted once by the workspace): the ring card «Ana te pide soporte remoto» —
//    «Rechazar» / «Aceptar» (the calls' MacIncomingCallSheet look), the viewer as a full-window panel, and the
//    floating notices («Otro de tus dispositivos ya aceptó…», «… terminó la sesión…»).
//  - `RemoteSupportPostRow`: the `custom_remote_support` DM post as a compact system-style row (icon + text,
//    «Unirse» while live and the reader is the helper).
import { ScreenAwake } from '../../../core/screenAwake';
import { screenAwake } from '../../screenAwake';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { remoteSupportPostProps, remoteSupportPostRow } from '../../../core/remoteSupportPost';
import type { PostRecord } from '../../../core/records';
import { t, tf } from '../../../i18n/i18n';
import { SupportViewer } from '../../../remotesupport/viewer';
import { Avatar } from '../../Avatar';
import { CallIcon } from '../calls/callIcons';
import { supportManager } from './supportManager';
import type { SupportNotice } from './supportManagerCore';
import './remoteSupport.css';

function useSupportState() {
  return useSyncExternalStore(
    (fn) => supportManager.subscribe(fn),
    () => supportManager.getState(),
  );
}

const NOTICE_TEXT: Record<Exclude<SupportNotice, null>, (name: string) => string> = {
  taken: () => t('web.rs.ring.taken'),
  acceptFailed: () => t('web.rs.ring.failed'),
  ended: () => t('web.rs.ended'),
  endedLink: (name) => tf('web.rs.ended.link', name || '?'),
  endedByPeer: (name) => (name ? tf('web.rs.ended.byPeer', name) : t('web.rs.ended')),
};

function RingCard() {
  const { ring } = useSupportState();
  if (!ring) return null;
  return (
    <div className="scrim incoming-call-scrim">
      <div className="incoming-call-card rs-ring" role="alertdialog" aria-label={t('web.rs.ring.title')} data-testid="rs-ring">
        <span className="incoming-call-avatar rs-ring-avatar">
          <Avatar userId={ring.requesterId} name={ring.requesterName || '?'} size={56} version={0} />
          <span className="rs-ring-badge" aria-hidden="true">
            <CallIcon name="screen" size={14} strokeWidth={2} />
          </span>
        </span>
        <h2>{t('web.rs.ring.title')}</h2>
        <p className="rs-ring-from">{tf('web.rs.ring.from', ring.requesterName || '?')}</p>
        <p className="rs-ring-hint">{t('web.rs.ring.hint')}</p>
        <div className="incoming-call-actions">
          <button type="button" className="incoming-decline" onClick={() => void supportManager.decline()} data-testid="rs-decline">
            <CallIcon name="x" size={16} strokeWidth={2} />
            {t('web.rs.ring.decline')}
          </button>
          <button type="button" className="incoming-accept" onClick={() => void supportManager.accept()} autoFocus data-testid="rs-accept">
            <CallIcon name="screen" size={16} strokeWidth={2} />
            {t('web.rs.ring.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The viewer: SupportViewer (plain DOM, shared with the Windows supporthost) in a full-window panel. */
function ViewerPanel() {
  const { viewer } = useSupportState();
  const host = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const session = supportManager.helperSession;
    if (!viewer || !host.current || !session || session.sessionId !== viewer.sessionId) return;
    const view = new SupportViewer({
      root: host.current,
      session,
      peerName: viewer.requesterName || '?',
      text: (key, ...args) => (args.length ? tf(key, ...args) : t(key)),
      onEnd: () => supportManager.end(),
    });
    const off = supportManager.subscribeView((s) => view.render(s));
    view.render(session.state);
    view.focus();
    // The helper watches a live screen: keep THIS one awake while the viewer is open (PARITY P304).
    screenAwake.hold(ScreenAwake.remoteSupport);
    return () => {
      screenAwake.release(ScreenAwake.remoteSupport);
      off();
      view.dispose();
    };
  }, [viewer?.sessionId, viewer?.requesterName]);
  if (!viewer) return null;
  return <div className="rs-viewer-panel" ref={host} role="dialog" aria-label={tf('web.rs.viewer.title', viewer.requesterName || '?')} />;
}

function Notice() {
  const { notice, noticeName } = useSupportState();
  useEffect(() => {
    if (!notice) return;
    const id = setTimeout(() => supportManager.consumeNotice(), 6000);
    return () => clearTimeout(id);
  }, [notice]);
  if (!notice) return null;
  return (
    <div className="rs-notice" role="status" data-testid="rs-notice">
      <CallIcon name="screen" size={16} />
      <span>{NOTICE_TEXT[notice](noticeName)}</span>
      <button type="button" className="icon-button" onClick={() => supportManager.consumeNotice()} aria-label={t('web.rs.end')}>
        <CallIcon name="x" size={14} />
      </button>
    </div>
  );
}

export function RemoteSupportHost({ me }: { me: string | null }) {
  useEffect(() => supportManager.wire(me), [me]);
  return (
    <>
      <ViewerPanel />
      <RingCard />
      <Notice />
    </>
  );
}

/** The `custom_remote_support` post (plugin-written, updated on every state change). */
export function RemoteSupportPostRow({ post, nameOf }: { post: PostRecord; nameOf: (id: string) => string }) {
  const state = useSupportState();
  const me = state.me;
  const props = remoteSupportPostProps(post.props);
  const row = remoteSupportPostRow(props, post.message, me, (id) => supportManager.nameOf(id) || nameOf(id));
  // «Unirse» only while the manager agrees the session is live and has no viewer here (the post can lag).
  const canJoin = row.joinable && state.joinable.includes(row.sessionId);
  return (
    <div className={`system-row rs-post${row.live ? ' live' : ''}`} data-testid="rs-post">
      <span className="rs-post-icon" aria-hidden="true">
        <CallIcon name="screen" size={14} strokeWidth={1.9} />
      </span>
      <span className="rs-post-text">{row.text}</span>
      {canJoin ? (
        <button type="button" className="rs-post-join" onClick={() => supportManager.join(row.sessionId)} data-testid="rs-join">
          {t('web.rs.post.join')}
        </button>
      ) : null}
    </div>
  );
}
